/**
 * Lender Submission Pack — dual PDF exporter (v3 · premium redesign).
 *
 * Both PDFs open with a full A4 cover page (brand + big client/lender/date),
 * then flow into the report body with a compact header + footer on every page.
 *
 * LENDER PDF  "Loan Submission Notes"
 *   Cover · Deal snapshot · 8 broker notes · Documents chips
 *
 * INTERNAL PDF  "Broker & Credit Team Report"  · flagged INTERNAL
 *   Cover (with readiness pill) · Deal snapshot · Key risks & flags panel ·
 *   Readiness banner · 8 notes · Documents · Lender checklist · QC ·
 *   Internal comments · Sign-off · Internal-use warning
 *
 * Filenames:
 *   Derwent_[ClientLastName]_[Lender]_Submission_Pack.pdf
 *   Derwent_[ClientLastName]_[Lender]_Internal_Report.pdf
 */
import jsPDF from 'jspdf';
import { LENDER_LOGOS, lookupLenderLogo } from './LenderLogo';

// ─── Bank logo pre-loader ─────────────────────────────────────
// Downloads the lender's brand logo as a data URL so jsPDF's addImage
// can stamp it onto the header + cover page. Returns null if the lender
// is unmapped or the fetch fails (the PDF will fall back to text).
const _fetchImageAsDataUrl = (url) => new Promise((resolve) => {
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve({
          dataUrl: canvas.toDataURL('image/png'),
          w: img.naturalWidth,
          h: img.naturalHeight,
        });
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  } catch { resolve(null); }
});

const preloadLenderLogo = async (lenderName) => {
  const rec = lookupLenderLogo(lenderName);
  if (!rec || !rec.src) return null;
  const loaded = await _fetchImageAsDataUrl(rec.src);
  if (!loaded) return null;
  return { ...loaded, ...rec };
};


// ─── Brand constants ───────────────────────────────────────────
// Broker identity for PDF headers/footers — set per workspace via setPdfBrand() (defaults to the primary Derwent Finance identity).
const BRAND = { business: 'Derwent Finance', acl: 'Australian Credit Licence 384324', abn: 'ABN 74 128 601 143' };
export const setPdfBrand = (b) => { if (!b || !b.business_name) return; BRAND.business = b.business_name; BRAND.acl = b.acl ? `Australian Credit Licence ${b.acl}`.replace('Licence ACL', 'Licence') : ''; BRAND.abn = b.abn ? `ABN ${b.abn}` : ''; };
const brandAcl = () => [BRAND.business, BRAND.acl].filter(Boolean).join(' · ');
const brandAbn = () => BRAND.abn;

// Palette
const NAVY   = [15, 23, 42];
const NAVY_D = [8, 15, 30];
const BLUE   = [37, 99, 235];
const LIGHT_BLUE = [147, 197, 253];
const INK    = [30, 41, 59];
const MUTED  = [100, 116, 139];
const SUBTLE = [148, 163, 184];
const RULE_LIGHT = [226, 232, 240];
const RULE_SOFT  = [241, 245, 249];
const AMBER_BORDER = [251, 191, 36];
const AMBER_BG     = [254, 243, 199];
const AMBER_INK    = [120, 53, 15];
const AMBER_MID    = [180, 83, 9];
const GREEN_BORDER = [110, 231, 183];
const GREEN_BG     = [236, 253, 245];
const GREEN_INK    = [6, 78, 59];
const GREEN_ACCENT = [16, 185, 129];
const RED         = [220, 38, 38];
const RED_INK     = [153, 27, 27];
const RED_BG      = [254, 226, 226];
const RED_BORDER  = [252, 165, 165];

// Layout
const PAGE_MARGIN  = 16;
const PAGE_W = 210;
const PAGE_H = 297;
const CONTENT_W = PAGE_W - PAGE_MARGIN * 2;
const HEADER_H  = 26;
const FOOTER_H  = 18;
const CONTENT_TOP = HEADER_H + 4;
const CONTENT_BOTTOM = PAGE_H - FOOTER_H;

// ─── Helpers ──────────────────────────────────────────────────
const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const lastNameOf = (name) => {
  if (!name) return 'Client';
  const parts = String(name).replace('&', ' ').split(/\s+/).filter(Boolean);
  return parts.length ? parts[parts.length - 1] : 'Client';
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(String(iso).length === 10 ? `${iso}T00:00:00` : iso)
      .toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return String(iso); }
};

const asString = (v) => String(v ?? '').trim();

const ensureRoom = (doc, needed) => {
  if ((doc._y ?? CONTENT_TOP) + needed > CONTENT_BOTTOM) {
    doc.addPage();
    drawHeaderOnCurrentPage(doc);
    doc._y = CONTENT_TOP;
  }
};

// ─── Header (drawn on every content page — cover skipped) ──────
const drawHeaderOnCurrentPage = (doc) => {
  const ctx = doc._headerCtx;
  if (!ctx) return;
  const { kind, pack } = ctx;
  // Solid deep-navy strip
  doc.setFillColor(...NAVY_D);
  doc.rect(0, 0, PAGE_W, HEADER_H, 'F');
  // Blue accent line
  doc.setFillColor(...BLUE);
  doc.rect(0, HEADER_H, PAGE_W, 0.8, 'F');
  // Left: brand
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(255, 255, 255);
  doc.text('DERWENT', PAGE_MARGIN, 12);
  doc.setTextColor(...LIGHT_BLUE);
  doc.text('FINANCE', PAGE_MARGIN + 26, 12);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(203, 213, 225);
  const label = kind === 'lender' ? 'Loan Submission Notes' : 'Broker & Credit Team Report';
  doc.text(label, PAGE_MARGIN, 18);
  doc.setFontSize(7);
  doc.setTextColor(148, 163, 184);
  const clientLine = `${pack.client_name || '—'}`;
  doc.text(clientLine, PAGE_MARGIN, 22.5);

  // Right: lender logo (if we have one) OR fallback text + kind pill
  const logo = ctx.logo;
  if (logo && logo.dataUrl) {
    // Fit inside a 34mm × 10mm slot in the top-right, preserving aspect
    const slotW = 34, slotH = 10;
    const scale = Math.min(slotW / (logo.w || slotW), slotH / (logo.h || slotH));
    const w = (logo.w || slotW) * scale;
    const h = (logo.h || slotH) * scale;
    const x = PAGE_W - PAGE_MARGIN - w;
    const y = 6 + (slotH - h) / 2;
    try {
      // Slight white plate behind the logo so it reads on the navy strip
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(x - 2, 5, w + 4, slotH + 2, 1.2, 1.2, 'F');
      doc.addImage(logo.dataUrl, 'PNG', x, y, w, h);
    } catch { /* if addImage throws for any reason, silently skip logo */ }
  } else {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.text(pack.lender || 'Lender', PAGE_W - PAGE_MARGIN, 12, { align: 'right' });
  }
  if (kind === 'internal') {
    doc.setFillColor(...RED);
    doc.roundedRect(PAGE_W - PAGE_MARGIN - 24, 18, 24, 6, 1.5, 1.5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(255, 255, 255);
    doc.text('INTERNAL', PAGE_W - PAGE_MARGIN - 12, 22, { align: 'center' });
  } else {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(203, 213, 225);
    doc.text('Assessor copy', PAGE_W - PAGE_MARGIN, 22, { align: 'right' });
  }
  if (pack.application_number) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.text(`App #${pack.application_number}`, PAGE_W - PAGE_MARGIN, 22.5, { align: 'right' });
  }
  doc.setTextColor(0, 0, 0);
};

// ─── Cover page — full A4, only shown once ─────────────────────
const drawCoverPage = (doc, pack, kind, template) => {
  const kindLabel = kind === 'lender' ? 'LOAN SUBMISSION NOTES' : 'BROKER & CREDIT TEAM REPORT';
  const kindSubtitle = kind === 'lender'
    ? 'Assessor copy · prepared for lender review'
    : 'Internal report · broker + credit team only';

  // Top navy panel
  doc.setFillColor(...NAVY_D);
  doc.rect(0, 0, PAGE_W, 105, 'F');
  // Blue accent lines
  doc.setFillColor(...BLUE);
  doc.rect(0, 105, PAGE_W, 1.8, 'F');
  doc.setFillColor(...LIGHT_BLUE);
  doc.rect(0, 107.5, PAGE_W, 0.5, 'F');

  // Faint pattern band
  doc.setFillColor(30, 41, 59);
  for (let x = 0; x < PAGE_W; x += 3) {
    doc.rect(x, 92, 0.4, 4, 'F');
  }

  // Brand
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(30);
  doc.setTextColor(255, 255, 255);
  doc.text('DERWENT', PAGE_MARGIN, 34);
  doc.setTextColor(...LIGHT_BLUE);
  doc.text('FINANCE', PAGE_MARGIN + 66, 34);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(203, 213, 225);
  doc.text('LOAN SUBMISSION PORTAL', PAGE_MARGIN, 42);

  // Big title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(24);
  doc.setTextColor(255, 255, 255);
  doc.text(kindLabel, PAGE_MARGIN, 66);

  // Subtitle
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(11);
  doc.setTextColor(...LIGHT_BLUE);
  doc.text(kindSubtitle, PAGE_MARGIN, 74);

  // Kind badge (top-right)
  const badgeW = 46;
  const badgeX = PAGE_W - PAGE_MARGIN - badgeW;
  if (kind === 'internal') {
    doc.setFillColor(...RED);
    doc.roundedRect(badgeX, 24, badgeW, 14, 2, 2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.text('INTERNAL', badgeX + badgeW / 2, 31, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.text('DO NOT SEND TO LENDER', badgeX + badgeW / 2, 34.5, { align: 'center' });
  } else {
    doc.setFillColor(...BLUE);
    doc.roundedRect(badgeX, 24, badgeW, 14, 2, 2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.text('ASSESSOR COPY', badgeX + badgeW / 2, 31, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.text('READY FOR LENDER', badgeX + badgeW / 2, 34.5, { align: 'center' });
  }

  // === Content section (white area) ===
  let y = 125;

  // "Prepared for" — lender name + logo
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text('PREPARED FOR', PAGE_MARGIN, y);

  const coverLogo = doc._headerCtx && doc._headerCtx.logo;
  if (coverLogo && coverLogo.dataUrl) {
    // Draw the logo above the lender text, up to 90mm × 24mm
    const slotW = 90, slotH = 24;
    const scale = Math.min(slotW / (coverLogo.w || slotW), slotH / (coverLogo.h || slotH));
    const w = (coverLogo.w || slotW) * scale;
    const h = (coverLogo.h || slotH) * scale;
    try {
      doc.addImage(coverLogo.dataUrl, 'PNG', PAGE_MARGIN, y + 3, w, h);
      y += h + 4;
    } catch {
      // if image fails, fall through to text-only rendering
      doc.setFontSize(22);
      doc.setTextColor(...NAVY);
      doc.text((pack.lender || 'Lender').toUpperCase(), PAGE_MARGIN, y + 11);
      y += 20;
    }
  } else {
    doc.setFontSize(22);
    doc.setTextColor(...NAVY);
    const lenderName = (pack.lender || 'Lender').toUpperCase();
    doc.text(lenderName, PAGE_MARGIN, y + 11);
    y += 20;
  }

  // Blue rule
  doc.setDrawColor(...BLUE);
  doc.setLineWidth(1);
  doc.line(PAGE_MARGIN, y, PAGE_MARGIN + 44, y);
  y += 8;

  // "Applicant(s)" — big hero
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text('APPLICANT(S)', PAGE_MARGIN, y);
  doc.setFontSize(20);
  doc.setTextColor(...NAVY);
  const applicants = (asString(pack.answers?.d_applicants) || pack.client_name || '—');
  const applicantLines = doc.splitTextToSize(applicants, CONTENT_W);
  doc.text(applicantLines.slice(0, 2), PAGE_MARGIN, y + 10);
  y += 14 + (applicantLines.length > 1 ? 10 : 0);

  // === Info card (3-column grid, 2 rows) ===
  y += 6;
  const cardH = 56;
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(...RULE_LIGHT);
  doc.setLineWidth(0.5);
  doc.roundedRect(PAGE_MARGIN, y, CONTENT_W, cardH, 3, 3, 'FD');
  // Left accent
  doc.setFillColor(...BLUE);
  doc.rect(PAGE_MARGIN, y, 3, cardH, 'F');

  const infoItems = [
    ['APPLICATION #',   asString(pack.application_number) || 'Not yet assigned'],
    ['DEAL #',          asString(pack.deal_number)        || 'Not yet assigned'],
    ['SUBMISSION DATE', fmtDate(asString(pack.answers?.d_date) || pack.created_at)],
    ['BROKER',          asString(pack.broker) || '—'],
    ['CREDIT TEAM',     asString(pack.credit_team_member) || '—'],
    ['REPORT GENERATED', new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })],
  ];
  const cellW = (CONTENT_W - 6) / 3;
  const rowGap = 24;
  infoItems.forEach((item, i) => {
    const col = i % 3;
    const row = Math.floor(i / 3);
    const cx = PAGE_MARGIN + 8 + col * cellW;
    const cy = y + 10 + row * rowGap;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.setTextColor(...MUTED);
    doc.text(item[0], cx, cy);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...NAVY);
    const valueLines = doc.splitTextToSize(String(item[1]), cellW - 6);
    doc.text(valueLines.slice(0, 2), cx, cy + 6);
  });
  y += cardH + 10;

  // Readiness pill on cover (internal only)
  if (kind === 'internal') {
    const gaps = buildOutstandingNarrative(pack, template);
    const ready = gaps.length === 0;
    doc.setFillColor(...(ready ? GREEN_BG : AMBER_BG));
    doc.setDrawColor(...(ready ? GREEN_BORDER : AMBER_BORDER));
    doc.setLineWidth(0.6);
    doc.roundedRect(PAGE_MARGIN, y, CONTENT_W, 15, 2.5, 2.5, 'FD');
    doc.setFillColor(...(ready ? GREEN_ACCENT : AMBER_MID));
    doc.rect(PAGE_MARGIN, y, 3, 15, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...(ready ? GREEN_INK : AMBER_INK));
    doc.text(ready
      ? 'READINESS · FILE READY TO PROCEED'
      : `READINESS · ${gaps.length} OUTSTANDING ITEM${gaps.length === 1 ? '' : 'S'} — SEE INTERNAL REPORT`,
      PAGE_MARGIN + 8, y + 9.5);
    y += 20;
  } else {
    // For lender PDF, positive framing chip
    doc.setFillColor(...GREEN_BG);
    doc.setDrawColor(...GREEN_BORDER);
    doc.setLineWidth(0.6);
    doc.roundedRect(PAGE_MARGIN, y, CONTENT_W, 15, 2.5, 2.5, 'FD');
    doc.setFillColor(...GREEN_ACCENT);
    doc.rect(PAGE_MARGIN, y, 3, 15, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...GREEN_INK);
    doc.text('SUBMISSION READY FOR ASSESSMENT', PAGE_MARGIN + 8, y + 9.5);
    y += 20;
  }

  // === Bottom of cover: brand strip ===
  const bottomY = PAGE_H - 32;
  // Divider
  doc.setDrawColor(...RULE_LIGHT);
  doc.setLineWidth(0.4);
  doc.line(PAGE_MARGIN, bottomY, PAGE_W - PAGE_MARGIN, bottomY);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...NAVY);
  doc.text(BRAND.business.toUpperCase(), PAGE_MARGIN, bottomY + 6);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...MUTED);
  doc.setFontSize(7.5);
  doc.text([brandAcl(), brandAbn()].filter(Boolean).join(' · '), PAGE_MARGIN, bottomY + 11);
  const confidentialLines = doc.splitTextToSize(
    'Confidential submission package. All personal and financial information contained herein is protected under the Privacy Act 1988 and remains the property of Derwent Finance and its clients.',
    CONTENT_W - 60,
  );
  doc.text(confidentialLines, PAGE_MARGIN, bottomY + 15);

  // Pack ref / version
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...MUTED);
  doc.text(`Pack ref · ${(pack.id || '').slice(-8).toUpperCase() || '—'}`, PAGE_W - PAGE_MARGIN, bottomY + 6, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...SUBTLE);
  doc.text(`Template v${template.version || '1.0'}`, PAGE_W - PAGE_MARGIN, bottomY + 11, { align: 'right' });

  doc._hasCoverPage = true;
};

// ─── Deal snapshot table (page 2 top) ──────────────────────────
const drawDealSnapshot = (doc, pack) => {
  const items = [
    ['Applicant(s)', asString(pack.answers?.d_applicants) || pack.client_name || '—'],
    ['Lender',       pack.lender || '—'],
    ['Broker',       pack.broker || '—'],
    ['Credit team',  pack.credit_team_member || '—'],
    ['Application #', asString(pack.application_number) || 'Not yet assigned'],
    ['Submission date', fmtDate(asString(pack.answers?.d_date) || pack.created_at)],
  ];
  ensureRoom(doc, 42);

  // Title bar
  doc.setFillColor(...NAVY);
  doc.rect(PAGE_MARGIN, doc._y, CONTENT_W, 8.5, 'F');
  doc.setFillColor(...BLUE);
  doc.rect(PAGE_MARGIN, doc._y, 2.5, 8.5, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(255, 255, 255);
  doc.text('DEAL SNAPSHOT', PAGE_MARGIN + 6, doc._y + 6);
  doc._y += 10;

  // 3-column table body
  const cellW = (CONTENT_W - 4) / 3;
  const rowH = 12;
  items.forEach((item, i) => {
    const col = i % 3;
    const row = Math.floor(i / 3);
    const x = PAGE_MARGIN + col * (cellW + 2);
    const y = doc._y + row * rowH;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...RULE_LIGHT);
    doc.setLineWidth(0.3);
    doc.roundedRect(x, y, cellW, rowH - 1, 1.5, 1.5, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.setTextColor(...MUTED);
    doc.text(item[0].toUpperCase(), x + 3, y + 4);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...NAVY);
    const valLines = doc.splitTextToSize(String(item[1]), cellW - 6);
    doc.text(valLines.slice(0, 1), x + 3, y + 8.5);
  });
  doc._y += rowH * 2 + 4;
};

// ─── Section title band (numbered) ─────────────────────────────
const sectionBand = (doc, title, { num = null } = {}) => {
  ensureRoom(doc, 13);
  doc.setFillColor(...NAVY);
  doc.rect(PAGE_MARGIN, doc._y, CONTENT_W, 8.5, 'F');
  doc.setFillColor(...BLUE);
  doc.rect(PAGE_MARGIN, doc._y, 2.5, 8.5, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  const label = (num !== null ? `${num}. ` : '') + title;
  doc.text(label.toUpperCase(), PAGE_MARGIN + 6, doc._y + 6);
  doc.setTextColor(0, 0, 0);
  doc._y += 12;
};

// Prose block — used for each broker-notes section
const drawProseBlock = (doc, label, body, { num }) => {
  const text = asString(body) || '—';
  ensureRoom(doc, 12);

  // Numbered dot + label
  if (num !== '') {
    doc.setFillColor(...NAVY);
    doc.circle(PAGE_MARGIN + 3.5, doc._y + 3.5, 3.5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.text(String(num), PAGE_MARGIN + 3.5, doc._y + 5, { align: 'center' });
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(...NAVY);
  doc.text(label, PAGE_MARGIN + (num !== '' ? 10 : 0), doc._y + 5);
  doc._y += 8;

  // Body
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...INK);
  // Split by paragraphs first for better spacing
  const paragraphs = text.split(/\n\s*\n/);
  paragraphs.forEach((para, idx) => {
    const bodyLines = doc.splitTextToSize(para.replace(/\n/g, ' '), CONTENT_W - 6);
    const bodyH = bodyLines.length * 4.4;
    ensureRoom(doc, bodyH + 2);
    doc.text(bodyLines, PAGE_MARGIN + 4, doc._y + 2);
    doc._y += bodyH + (idx < paragraphs.length - 1 ? 3 : 4);
  });

  // Soft rule
  doc.setDrawColor(...RULE_LIGHT);
  doc.setLineWidth(0.2);
  doc.line(PAGE_MARGIN, doc._y, PAGE_MARGIN + CONTENT_W, doc._y);
  doc._y += 5;
};

const BROKER_NOTE_ORDER = ['n1', 'n2', 'n3', 'n4', 'n5', 'n6', 'n7', 'n8'];
const BROKER_NOTE_LABELS = {
  n1: 'Summary',
  n2: 'Income, employment & servicing',
  n3: 'Security',
  n4: 'Funds to complete',
  n5: 'Living expenses',
  n6: 'Credit report',
  n7: 'Exit strategy',
  n8: 'Other outstanding matters',
};

const drawBrokerNotes = (doc, pack, template, { dropEmptyOther = true } = {}) => {
  sectionBand(doc, 'Broker notes — loan submission');
  const notesSection = template.sections.find(s => s.id === 'notes');
  const orderedFields = notesSection?.fields || [];
  const fieldMap = new Map(orderedFields.map(f => [f.id, f]));
  const answers = pack.answers || {};

  let num = 1;
  BROKER_NOTE_ORDER.forEach(id => {
    const field = fieldMap.get(id);
    if (!field) return;
    const label = BROKER_NOTE_LABELS[id] || field.label;
    const val = answers[id];
    if (id === 'n8' && dropEmptyOther && !asString(val)) return;
    drawProseBlock(doc, label, val, { num });
    num += 1;
  });
};

// Document chips
const drawDocumentChips = (doc, pack, template) => {
  const docsSection = template.sections.find(s => s.id === 'documents');
  if (!docsSection) return;
  const answers = pack.answers || {};
  const selected = docsSection.fields
    .filter(f => (f.type === 'checkbox' && answers[f.id]))
    .map(f => stripHtml(f.label));
  const other = asString(answers['doc_other']);
  if (other) selected.push(other);
  if (selected.length === 0) return;

  sectionBand(doc, 'Documents submitted with this application');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  let x = PAGE_MARGIN;
  let y = doc._y;
  const chipH = 7;
  const padX = 3;

  selected.forEach(txt => {
    const w = doc.getTextWidth(txt) + padX * 2 + 4;
    if (x + w > PAGE_MARGIN + CONTENT_W) {
      x = PAGE_MARGIN;
      y += chipH + 2.5;
      ensureRoom(doc, chipH + 4);
    }
    doc.setFillColor(...BLUE);
    doc.roundedRect(x, y, w, chipH, 3.5, 3.5, 'F');
    doc.setTextColor(255, 255, 255);
    doc.text('\u2713', x + padX - 0.5, y + 4.8);
    doc.text(txt, x + padX + 3, y + 4.8);
    x += w + 3;
  });
  doc._y = y + chipH + 6;
};

// Per-page footer — always skips the cover page
const drawFooter = (doc, pack, kind) => {
  const pageCount = doc.internal.getNumberOfPages();
  const startPage = doc._hasCoverPage ? 2 : 1;
  const contentTotal = pageCount - (doc._hasCoverPage ? 1 : 0);
  for (let i = startPage; i <= pageCount; i++) {
    doc.setPage(i);
    const y0 = PAGE_H - FOOTER_H;
    doc.setDrawColor(...RULE_LIGHT);
    doc.setLineWidth(0.3);
    doc.line(PAGE_MARGIN, y0 + 2, PAGE_W - PAGE_MARGIN, y0 + 2);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...NAVY);
    doc.text(`Prepared by ${pack.broker || '—'}`, PAGE_MARGIN, y0 + 6.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...MUTED);
    doc.text(brandAcl(), PAGE_MARGIN, y0 + 10);
    doc.text(brandAbn(), PAGE_MARGIN, y0 + 13.5);

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...NAVY);
    const displayNum = doc._hasCoverPage ? (i - 1) : i;
    doc.text(`Page ${displayNum} of ${contentTotal}`, PAGE_W - PAGE_MARGIN, y0 + 6.5, { align: 'right' });
    if (kind === 'internal') {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...RED);
      doc.text('INTERNAL — NOT FOR LENDER', PAGE_W - PAGE_MARGIN, y0 + 13.5, { align: 'right' });
    } else {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...MUTED);
      doc.text(new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }),
        PAGE_W - PAGE_MARGIN, y0 + 13.5, { align: 'right' });
    }
    doc.setTextColor(0, 0, 0);
  }
};

// ─── LENDER PDF ─────────────────────────────────────────────
export const generateLenderPdf = async (pack, template) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const logo = await preloadLenderLogo(pack.lender);
  doc._headerCtx = { kind: 'lender', pack, logo };

  // Page 1 · Cover (no header)
  drawCoverPage(doc, pack, 'lender', template);

  // Page 2 · Content starts with normal header
  doc.addPage();
  drawHeaderOnCurrentPage(doc);
  doc._y = CONTENT_TOP;

  drawDealSnapshot(doc, pack);
  drawBrokerNotes(doc, pack, template, { dropEmptyOther: true });
  drawDocumentChips(doc, pack, template);
  drawFooter(doc, pack, 'lender');

  doc.save(`Derwent_${lastNameOf(pack.client_name)}_${(pack.lender || 'Lender').replace(/\s+/g, '_')}_Submission_Pack.pdf`);
};

// ─── INTERNAL PDF ───────────────────────────────────────────

function buildOutstandingNarrative(pack, template) {
  const naGroups = pack.na_groups || {};
  const answers = pack.answers || {};
  const gaps = [];
  template.sections.forEach(section => {
    if (!section.lender_visible) return;
    if (section.kind === 'signoff') return; // sign-off removed from UI + counting
    if (section.kind === 'notes') {
      section.fields.forEach(f => {
        if (f.mandatory && !asString(answers[f.id])) {
          const label = stripHtml(f.label);
          gaps.push(`${label} not yet written`);
        }
      });
    } else if (section.kind === 'checklist') {
      section.groups.forEach(group => {
        if (group.na && naGroups[group.id]) return;
        if (group.any_of) {
          const doneCount = group.fields.filter(f => !!answers[f.id]).length;
          if (doneCount === 0) {
            gaps.push(`${group.id}. ${group.name} — choose one of ${group.fields.length} options`);
          }
        } else {
          const missing = group.fields.filter(f => f.mandatory && !answers[f.id]);
          missing.forEach(f => gaps.push(`${group.id}. ${stripHtml(f.label)}`));
        }
      });
    }
  });
  return gaps;
}

// Categorise gaps as HIGH (blocking) vs MEDIUM (soft)
function categoriseGaps(pack, template) {
  const naGroups = pack.na_groups || {};
  const answers = pack.answers || {};
  const high = [];
  const medium = [];
  template.sections.forEach(section => {
    if (!section.lender_visible) return;
    if (section.kind === 'signoff') return; // sign-off removed
    if (section.kind === 'notes') {
      section.fields.forEach(f => {
        if (f.mandatory && !asString(answers[f.id])) medium.push(`Broker note not written: ${stripHtml(f.label)}`);
      });
    } else if (section.kind === 'checklist') {
      section.groups.forEach(group => {
        if (group.na && naGroups[group.id]) return;
        if (group.any_of) {
          const doneCount = group.fields.filter(f => !!answers[f.id]).length;
          if (doneCount === 0) high.push(`${group.id}. ${group.name} — choose one of ${group.fields.length} options`);
        } else {
          const missing = group.fields.filter(f => f.mandatory && !answers[f.id]);
          missing.forEach(f => high.push(`${group.id}. ${stripHtml(f.label)}`));
        }
      });
    }
  });
  return { high, medium };
}

// KEY RISKS & FLAGS box — internal only. Sits above readiness banner.
const drawKeyRisksBox = (doc, pack, template) => {
  const { high, medium } = categoriseGaps(pack, template);
  // Also flag MIRs / lodgement risks
  const answers = pack.answers || {};
  const flags = [];
  // Missing application/deal number
  if (!pack.application_number) flags.push('Application # not assigned');
  if (!pack.deal_number) flags.push('Deal # not assigned');
  // No documents ticked
  const docsSection = template.sections.find(s => s.id === 'documents');
  if (docsSection) {
    const anyDoc = docsSection.fields.some(f => f.type === 'checkbox' && answers[f.id]) || asString(answers['doc_other']);
    if (!anyDoc) flags.push('No documents ticked as submitted with application');
  }
  const total = high.length + flags.length;
  if (total === 0) return;

  const items = [
    ...high.map(x => ({ tone: 'red', text: x })),
    ...flags.map(x => ({ tone: 'amber', text: x })),
  ];

  ensureRoom(doc, 14);
  sectionBand(doc, 'Key risks & flags');

  const wrapped = items.map(it => {
    const lines = doc.splitTextToSize(`\u2022  ${it.text}`, CONTENT_W - 22);
    return { tone: it.tone, lines };
  });
  const bodyH = wrapped.reduce((s, w) => s + w.lines.length * 4.2, 0) + 6;
  ensureRoom(doc, bodyH + 4);

  // Card
  doc.setFillColor(...RED_BG);
  doc.setDrawColor(...RED_BORDER);
  doc.setLineWidth(0.5);
  doc.roundedRect(PAGE_MARGIN, doc._y, CONTENT_W, bodyH, 2.5, 2.5, 'FD');
  doc.setFillColor(...RED);
  doc.rect(PAGE_MARGIN, doc._y, 3, bodyH, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...RED_INK);
  doc.text(`${high.length} CRITICAL · ${flags.length} FLAG${flags.length === 1 ? '' : 'S'}`, PAGE_MARGIN + 8, doc._y + 5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  let by = doc._y + 10;
  wrapped.forEach(w => {
    doc.setTextColor(...(w.tone === 'red' ? RED_INK : AMBER_INK));
    doc.text(w.lines, PAGE_MARGIN + 8, by);
    by += w.lines.length * 4.2;
  });
  doc._y += bodyH + 5;
};

const drawReadinessBanner = (doc, pack, template) => {
  const gaps = buildOutstandingNarrative(pack, template);
  const ready = gaps.length === 0;
  const ink   = ready ? GREEN_INK    : AMBER_INK;
  const bg    = ready ? GREEN_BG     : AMBER_BG;
  const brd   = ready ? GREEN_BORDER : AMBER_BORDER;
  const accent = ready ? GREEN_ACCENT : AMBER_MID;

  const titleTxt = ready
    ? 'FILE READY TO PROCEED'
    : `NOT READY · ${gaps.length} OUTSTANDING ITEM${gaps.length === 1 ? '' : 'S'}`;
  const linesArr = ready
    ? ['All mandatory items complete. Sign-off, checklist and QC verified.']
    : gaps;

  const wrappedLines = linesArr.flatMap(l => doc.splitTextToSize(`\u2022  ${l}`, CONTENT_W - 22));
  const bodyH = wrappedLines.length * 4.2;
  const height = 12 + bodyH + 4;

  ensureRoom(doc, height + 5);
  doc.setFillColor(...bg);
  doc.setDrawColor(...brd);
  doc.setLineWidth(0.5);
  doc.roundedRect(PAGE_MARGIN, doc._y, CONTENT_W, height, 2.5, 2.5, 'FD');
  doc.setFillColor(...accent);
  doc.rect(PAGE_MARGIN, doc._y, 3, height, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(...ink);
  doc.text(titleTxt, PAGE_MARGIN + 8, doc._y + 7);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...ink);
  doc.text(wrappedLines, PAGE_MARGIN + 8, doc._y + 13.5);
  doc._y += height + 6;
};

// Checklist status item
const drawChecklistItem = (doc, status, text, indent = 0) => {
  const bodyLines = doc.splitTextToSize(text, CONTENT_W - 14 - indent);
  const rowH = Math.max(6, bodyLines.length * 4 + 2);
  ensureRoom(doc, rowH + 1);
  const x0 = PAGE_MARGIN + indent;
  const [boxFill, boxDraw, boxInkCol] = {
    tick:     [BLUE,          BLUE,          [255, 255, 255]],
    outline:  [AMBER_BG,      AMBER_BORDER,  AMBER_MID],
    neutral:  [[248, 250, 252], SUBTLE,      SUBTLE],
    waived:   [[248, 250, 252], SUBTLE,      MUTED],
  }[status];
  doc.setFillColor(...boxFill);
  doc.setDrawColor(...boxDraw);
  doc.setLineWidth(0.4);
  doc.roundedRect(x0, doc._y, 5, 5, 0.8, 0.8, 'FD');
  if (status === 'tick') {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...boxInkCol);
    doc.text('\u2713', x0 + 1, doc._y + 3.9);
  } else if (status === 'waived') {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...boxInkCol);
    doc.text('\u2014', x0 + 1, doc._y + 3.9);
  }
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(status === 'neutral' ? MUTED[0] : INK[0], status === 'neutral' ? MUTED[1] : INK[1], status === 'neutral' ? MUTED[2] : INK[2]);
  doc.text(bodyLines, x0 + 8, doc._y + 4);
  doc._y += rowH;
};

const drawChecklistSection = (doc, section, pack) => {
  sectionBand(doc, section.title);
  const answers = pack.answers || {};
  const naGroups = pack.na_groups || {};

  section.groups.forEach(group => {
    const gid = group.id;
    const isNa = group.na && naGroups[gid];

    ensureRoom(doc, 12);
    // Group heading strip
    doc.setFillColor(...RULE_SOFT);
    doc.setDrawColor(...RULE_LIGHT);
    doc.roundedRect(PAGE_MARGIN, doc._y, CONTENT_W, 8, 1.5, 1.5, 'FD');
    // Letter chip
    doc.setFillColor(...NAVY);
    doc.roundedRect(PAGE_MARGIN + 2, doc._y + 1.5, 5.5, 5, 1, 1, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(255, 255, 255);
    doc.text(gid, PAGE_MARGIN + 4.75, doc._y + 5, { align: 'center' });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(...NAVY);
    doc.text(group.name, PAGE_MARGIN + 10, doc._y + 5.5);

    // Right-side status label
    if (isNa) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(...MUTED);
      doc.text('DELIBERATELY WAIVED', PAGE_W - PAGE_MARGIN - 3, doc._y + 5.5, { align: 'right' });
    } else if (group.any_of) {
      const doneCount = group.fields.filter(f => !!answers[f.id]).length;
      const satisfied = doneCount > 0;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(...(satisfied ? BLUE : AMBER_MID));
      const rightLabel = satisfied ? `SATISFIED · ${doneCount} PICKED` : `CHOOSE ONE OF ${group.fields.length}`;
      doc.text(rightLabel, PAGE_W - PAGE_MARGIN - 3, doc._y + 5.5, { align: 'right' });
    } else {
      const missing = group.fields.filter(f => f.mandatory && !answers[f.id]).length;
      if (missing > 0) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(...AMBER_MID);
        doc.text(`${missing} OUTSTANDING`, PAGE_W - PAGE_MARGIN - 3, doc._y + 5.5, { align: 'right' });
      } else {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(...GREEN_ACCENT);
        doc.text('COMPLETE', PAGE_W - PAGE_MARGIN - 3, doc._y + 5.5, { align: 'right' });
      }
    }
    doc._y += 10;

    // Fine print
    if (group.fine) {
      const fine = doc.splitTextToSize(stripHtml(group.fine), CONTENT_W - 6);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(7.5);
      doc.setTextColor(...MUTED);
      ensureRoom(doc, fine.length * 3.5 + 2);
      doc.text(fine, PAGE_MARGIN + 4, doc._y);
      doc._y += fine.length * 3.5 + 2;
    }

    if (isNa) { doc._y += 2; return; }

    const anyDone = group.any_of && group.fields.some(f => !!answers[f.id]);
    group.fields.forEach(f => {
      const picked = !!answers[f.id];
      let status;
      if (picked) status = 'tick';
      else if (group.any_of && anyDone) status = 'neutral';
      else if (f.mandatory || group.any_of) status = 'outline';
      else status = 'neutral';
      drawChecklistItem(doc, status, stripHtml(f.label), 4);
    });

    doc._y += 3;
  });
};

const drawInternalWarning = (doc, pack, template) => {
  const meta = template.meta || {};
  const lines = [
    `Source: ${meta.source_doc || (template.lender + ' submission checklist')} · Version ${template.version || '—'}`,
    meta.portal ? `Portal: ${meta.portal}` : null,
    meta.phone ? `Contact: ${meta.phone}` : null,
    meta.sla ? `SLA: ${meta.sla}` : null,
    `Generated: ${new Date().toLocaleString('en-AU', { timeZone: 'Australia/Sydney', dateStyle: 'medium', timeStyle: 'short' })}`,
  ].filter(Boolean);

  const bodyLines = lines.flatMap(l => doc.splitTextToSize(l, CONTENT_W - 12));
  const bodyH = bodyLines.length * 4;
  const h = 11 + bodyH;
  ensureRoom(doc, h + 4);

  doc.setFillColor(...RED_BG);
  doc.setDrawColor(...RED);
  doc.setLineWidth(0.5);
  doc.roundedRect(PAGE_MARGIN, doc._y, CONTENT_W, h, 2, 2, 'FD');
  doc.setFillColor(...RED);
  doc.rect(PAGE_MARGIN, doc._y, 3, h, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...RED_INK);
  doc.text('INTERNAL USE ONLY — DO NOT SEND TO LENDER', PAGE_MARGIN + 8, doc._y + 6);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(bodyLines, PAGE_MARGIN + 8, doc._y + 12);
  doc._y += h + 5;
};

const drawInternalComments = (doc, pack, template) => {
  const section = template.sections.find(s => s.id === 'internal_comments');
  // Support both new thread-style comments AND the legacy single-field
  const thread = Array.isArray(pack.internal_comments) ? pack.internal_comments : [];
  const legacy = asString((pack.answers || {}).i_comments);
  if (!section || (thread.length === 0 && !legacy)) return;
  sectionBand(doc, 'Internal comments · risks, exceptions, recommendations');

  // Legacy blob first (if present)
  if (legacy) drawProseBlock(doc, 'Legacy comment (single-field)', legacy, { num: '' });

  // Chronological thread (oldest → newest)
  const sorted = [...thread].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  sorted.forEach((c, i) => {
    ensureRoom(doc, 18);
    const ts = c.created_at ? new Date(c.created_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
    // Header row: index + author + timestamp
    doc.setFillColor(...RULE_SOFT);
    doc.setDrawColor(...RULE_LIGHT);
    doc.roundedRect(PAGE_MARGIN, doc._y, CONTENT_W, 7, 1.2, 1.2, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...NAVY);
    doc.text(`#${i + 1} · ${c.user_name || 'Unknown'}`, PAGE_MARGIN + 3, doc._y + 4.8);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...MUTED);
    doc.text(ts, PAGE_W - PAGE_MARGIN - 3, doc._y + 4.8, { align: 'right' });
    doc._y += 8;

    // Body
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(...INK);
    const lines = doc.splitTextToSize(c.text || '', CONTENT_W - 8);
    ensureRoom(doc, lines.length * 4.3 + 2);
    doc.text(lines, PAGE_MARGIN + 4, doc._y + 3);
    doc._y += lines.length * 4.3 + 5;
  });
};

const drawSignoff = (doc, pack, template) => {
  const section = template.sections.find(s => s.id === 'signoff');
  if (!section) return;
  sectionBand(doc, 'Sign-off');
  const answers = pack.answers || {};
  const rowH = 13;
  ensureRoom(doc, rowH * section.fields.length + 3);
  section.fields.forEach((f) => {
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...RULE_LIGHT);
    doc.roundedRect(PAGE_MARGIN, doc._y, CONTENT_W, rowH - 1, 1.5, 1.5, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...MUTED);
    doc.text(f.label.toUpperCase(), PAGE_MARGIN + 4, doc._y + 5);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...NAVY);
    const val = f.type === 'date' ? fmtDate(answers[f.id]) : (answers[f.id] || '—');
    doc.text(val, PAGE_MARGIN + 48, doc._y + 8.5);
    doc._y += rowH;
  });
  doc._y += 3;
};

export const generateInternalPdf = async (pack, template) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const logo = await preloadLenderLogo(pack.lender);
  doc._headerCtx = { kind: 'internal', pack, logo };

  // Page 1 · Cover
  drawCoverPage(doc, pack, 'internal', template);

  // Page 2 · Content
  doc.addPage();
  drawHeaderOnCurrentPage(doc);
  doc._y = CONTENT_TOP;

  drawDealSnapshot(doc, pack);
  drawKeyRisksBox(doc, pack, template);
  drawReadinessBanner(doc, pack, template);
  drawBrokerNotes(doc, pack, template, { dropEmptyOther: false });
  drawDocumentChips(doc, pack, template);

  const checklist = template.sections.find(s => s.id === 'checklist');
  if (checklist) drawChecklistSection(doc, checklist, pack);
  const qc = template.sections.find(s => s.id === 'qc');
  if (qc) drawChecklistSection(doc, qc, pack);
  drawInternalComments(doc, pack, template);
  drawInternalWarning(doc, pack, template);

  drawFooter(doc, pack, 'internal');
  doc.save(`Derwent_${lastNameOf(pack.client_name)}_${(pack.lender || 'Lender').replace(/\s+/g, '_')}_Internal_Report.pdf`);
};
